import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { List, Box, Tooltip, IconButton, Typography, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useAddChatMutation, useGetChatsQuery } from '../chatApiSlice';
import { selectCurrentUser, setChatStatus } from '../../Auth/authSlice';
import ChatItem from './ChatItem';
import { groupChatsByDate } from '../utility/groupChatsByDate';
import StyledDateDivider from './StyledDateDivider';
import { Element } from 'react-scroll';
import { KeyboardArrowDown } from '@mui/icons-material';
import { SkeletonChatList } from '../skeletons/SkeletonChatList';
import Overlay from './Overlay';
import { useParams } from 'react-router-dom';
import chatGif from '../../../assets/Gifs/chat.png';
import hiGif from '../../../assets/Gifs/hi.gif';

import toast from 'react-hot-toast';
import useDetectKeyboardOpen from 'use-detect-keyboard-open';

const ChatList = ({ setEditingChat, showScrollButton, setShowScrollButton, inputHeight, disabled, setDisabled }) => {
  const user = useSelector(selectCurrentUser);
  const { empCode } = useParams();
  const { data: chatData, isLoading, error } = useGetChatsQuery(empCode);
  const listRef = useRef(null);
  const dispatch = useDispatch();
  const [addChat] = useAddChatMutation();
  const [loading, setLoading] = useState(false);
  const isKeyboardOpen = useDetectKeyboardOpen();

  useEffect(() => {
    setLoading(true);
  }, [empCode]);

  useEffect(() => {
    if (chatData !== undefined) {
      setLoading(false);
    }
  }, [chatData]);

  useLayoutEffect(() => {
    dispatch(setChatStatus(false));
  }, [dispatch]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight + 300;
    }
    dispatch(setChatStatus(false));
  }, [chatData, dispatch]);

  const groupedChats = groupChatsByDate(chatData);

  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 65;
      setShowScrollButton(!isAtBottom);
    }
  };

  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, [inputHeight]);
  useEffect(() => {
    window.addEventListener('resize', scrollToBottom);
    return () => window.removeEventListener('resize', scrollToBottom);
  }, []);
  const handleSendHello = async () => {
    try {
      const formData = new FormData();
      formData.append('message', 'Hello 👋');
      formData.append('receiverCode', empCode);
      const response = await fetch(hiGif);
      const blob = await response.blob();
      const file = new File([blob], 'hi.gif', { type: blob.type });
      formData.append('attachments', file);
      // await toast.promise(addChat(formData).unwrap(), {
      //   loading: 'Sending message...',
      //   success: 'Message sent successfully!',
      //   error: 'Failed to send message. Please try again.',
      // });
      await addChat(formData).unwrap();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };
  if (isLoading || loading) return null;
  if (error)
    return (
      <>
        <SkeletonChatList />
        <Overlay />
      </>
    );

  return (
    <Box
      sx={{
        width: '100%',
        boxShadow: 'none',
        height: '100%',
      }}
    >
      {chatData.length > 0 && !disabled ? (
        <List
          id="chat-list-container"
          ref={listRef}
          sx={{
            overflow: 'auto',
            padding: 0,
            height: isKeyboardOpen ? `calc(80vh - ${inputHeight}px)` : `calc(90vh - ${inputHeight}px)`,
          }}
          onScroll={handleScroll}
        >
          {Object.entries(groupedChats).map(([date, chats]) => (
            <React.Fragment key={date}>
              <StyledDateDivider date={date} />
              {chats.map((chat) => (
                <Element key={`chat-${chat._id}`} name={`chat-${chat._id}`}>
                  <ChatItem chat={chat} user={user} setEditingChat={setEditingChat} />
                </Element>
              ))}
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <img src={chatGif} alt="No Chats" style={{ height: 200, width: 200 }} />
          <Typography variant="subtitle1" sx={{ mt: 2, color: 'grey' }}>
            {disabled ? '' : 'No messages yet'}
          </Typography>
          <Button
            variant="standard"
            onClick={handleSendHello}
            disabled={disabled}
            sx={{
              mt: 1,
              borderRadius: '14px',
              outline: 'none',
              border: 'none',
              backgroundColor: '#EEF5FF',
              textTransform: 'none',
              color: 'black',
              '&:hover': {
                outline: 'none',
                border: 0,
              },
            }}
          >
            {disabled ? 'Coming soon...' : 'Say Hello 👋'}
          </Button>
        </Box>
      )}
      {showScrollButton ? (
        <Tooltip title="Scroll to bottom" placement="left">
          <IconButton
            onClick={scrollToBottom}
            sx={{
              position: 'absolute',
              bottom: 2,
              right: 16,
              backgroundColor: 'lightgray',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'grey.300',
              },
              zIndex: 1000,
              color: 'black',
            }}
          >
            <KeyboardArrowDown sx={{ m: '-5px' }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default ChatList;
